import { Injectable } from '@angular/core';
import { UAParser } from 'ua-parser-js';

@Injectable({ providedIn: 'root' })
export class BlockDeviceService {
  // eslint-disable-next-line no-undef
  parser = new UAParser();
  device = this.parser.getResult();
  ruleTypes = {
    number: 'number',
  };

  private parse(str: string) {
    const rule: any = {
      device: {},
      browser: {},
      os: {},
    };

    // --------------------------
    // -- Device
    // --------------------------
    const deviceRegex = /Device:(\w|\d|\s)+(\/[\d|\d.\d]*)*/gim;
    const devicesMatches = str.match(deviceRegex);

    if (devicesMatches) {
      const n = devicesMatches[0]
        .trim()
        .replace(/Device:/gim, '')
        .trim()
        .split('/');
      rule.device = {
        name: n[0],
        version: n[1],
      };
    }

    const deviceTypeRegex = /Device-Type:(\w|\d|\s)+(\/[\d|\d.\d]*)*/gim;
    const devicesTypesMatches = str.match(deviceTypeRegex);

    if (devicesTypesMatches) {
      const n = devicesTypesMatches[0]
        .trim()
        .replace(/Device-Type:/gim, '')
        .trim()
        .split('/');
      rule.device.type = n[0];
    }

    // --------------------------
    // -- Browser
    // --------------------------
    const browserRegex = /Browser:(\w|\d|\s)+(\/[\d|\d.\d]*)*/gim;
    const browsersMatches = str.match(browserRegex);

    if (browsersMatches) {
      const n = browsersMatches[0]
        .trim()
        .replace(/Browser:/gim, '')
        .trim()
        .split('/');
      rule.browser = {
        name: n[0],
        version: n[1],
      };
    }

    // --------------------------
    // -- OS
    // --------------------------
    const osRegex = /Os:(\w|\d|\s)+(\/[\d|\d.\d]*)*/gim;
    const osMatches = str.match(osRegex);

    if (osMatches) {
      const n = osMatches[0].trim().replace(/Os:/gim, '').trim().split('/');
      rule.os = {
        name: n[0],
        version: n[1],
      };
    }

    return [rule];
  }

  private compareVersion(expected: any, value: any) {
    const a = expected.split('.');
    const b = value.split('.');
    const iterations = a.length <= b.length ? a.length : b.length;

    for (let i = 0; i < iterations; i++) {
      const e = a[i];
      const v = b[i];

      if (e == '*') {
        // match with all versions
        return 1;
      }

      const r1 = /^(\d)*$/i;

      if (r1.test(e) && r1.test(v)) {
        const n = parseInt(e, 10);
        const n2 = parseInt(v, 10);

        // compare
        if (n > n2) {
          return 1;
        } else if (n < n2) {
          return -1;
        }
      }
    }

    return 0;
  }

  private check(rule: any) {
    // verification list
    // [{ expected: 'string', value: 'string' }, ...]
    const list = [];

    // check for os restriction
    if (rule.os) {
      if (rule.os.name) {
        list.push({
          expected: rule.os.name.trim(),
          value: this.device.os.name,
        });

        if (rule.os.version) {
          list.push({
            expected: rule.os.version,
            value: this.device.os.version,
            type: this.ruleTypes.number,
          });
        }
      }
    }

    // check for browser restriction
    if (rule.browser) {
      if (rule.browser.name) {
        list.push({
          expected: rule.browser.name.trim(),
          value: this.device.browser.name,
        });

        if (rule.browser.version) {
          list.push({
            expected: rule.browser.version,
            value: this.device.browser.version,
            type: this.ruleTypes.number,
          });
        }
      }
    }

    // check device browser restriction
    if (rule.device) {
      if (rule.device.model) {
        // SE, S20, G8, ....
        list.push({
          expected: rule.device.model.trim(),
          value: this.device.device.model,
        });
      }
      if (rule.device.brand) {
        // Apple, Samsung, Microsoft, ...
        list.push({
          expected: rule.device.brand.trim(),
          value: this.device.device.vendor, // brand
        });
      }
      if (rule.device.type) {
        // Phone, Tablet, Desktop, ...
        list.push({
          expected: rule.device.type.trim(),
          value: this.device.device.type,
        });
      }
    }

    if (list.length == 0) {
      return false;
    }

    let blocked = false;

    //
    // All rules must be true for the blocking condition to be met, since it
    // is a multiple combinations of key of string rule
    //
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (item.expected && item.value) {
        // if expected value is * then block is true
        if (item.expected == '*') {
          blocked = true;
        } else {
          if (item.type == this.ruleTypes.number) {
            const result = this.compareVersion(item.expected, item.value);
            blocked = result == 0;
          } else {
            const a = item.expected.trim().toLowerCase();
            const b = item.value.trim().toLowerCase();

            if (b.indexOf(a) >= 0) {
              // Safari == Mobile Safari
              blocked = true;
            } else if (a == b) {
              blocked = true;
            } else {
              blocked = false;
            }
          }
        }
      }

      if (blocked == false) {
        break;
      }
    }

    return blocked;
  }

  isBlocked(rules: any) {
    if (rules && rules.length) {
      // Step 1: parse rules
      let restrictions: any[] = [];
      for (let i = 0; i < rules.length; i++) {
        const r = rules[i];
        const p = this.parse(r);
        restrictions = restrictions.concat(p);
      }
      // // Step 2: Verify all rules
      for (let i = 0; i < restrictions.length; i++) {
        const r = restrictions[i];
        const blocked = this.check(r);
        if (blocked) {
          return true;
        }
      }
    }

    return false;
  }
}
